// TypeScript port of Morpho Midnight `TickLib` (tick ⇄ price ⇄ fixed rate).
//
// Source: morpho-org/sdks `packages/midnight-sdk/src/math/TickLib.ts`, itself a
// port of the on-chain `src/libraries/TickLib.sol`. Kept dependency-free (no
// @morpho-org/* import) so margin-fetcher owns the math. Covered by math.test.ts.
//
// Midnight prices are WAD-scaled zero-coupon discount factors in (0, 1]:
// paying `price · units` now settles `units` of loan token at maturity. The
// implied period rate is `1/price - 1`; the displayed APR annualizes it over
// the seconds remaining to maturity (simple, matching the SDK's `tickToApr`).

export const WAD = 1_000_000_000_000_000_000n // 1e18
const WAD_SQUARED = 1_000_000_000_000_000_000_000_000_000_000_000_000n // 1e36

// Midnight tick-curve constants (WAD-scaled where noted).
export const LN_ONE_PLUS_DELTA = 4_987_541_511_039_073n
export const LN_2 = 693_147_180_559_945_309n
export const EXP_OFFSET = 322_611_214_989_459_870n
export const MAX_TICK = 6744n
export const PRICE_ROUNDING_STEP = 100_000_000_000n

// morpho-ts `Time.s.from.y(1n)` = 365 * 24 * 60 * 60.
export const SECONDS_PER_YEAR = 31_536_000n

const mulDivUp = (x: bigint, y: bigint, d: bigint) => (x * y + (d - 1n)) / d

const divHalfDownUnchecked = (x: bigint, d: bigint) => (x + (d - 1n) / 2n) / d

/** WAD-scaled exponential approximation used by the Midnight tick curve. */
export function wExp(x: bigint): bigint {
  if (x < 0n) return WAD_SQUARED / wExp(-x)

  const q = (x + EXP_OFFSET) / LN_2
  const r = x - q * LN_2
  const secondTerm = (r * r) / (2n * WAD)
  const thirdTerm = (secondTerm * r) / (3n * WAD)
  const expR = WAD + r + secondTerm + thirdTerm

  return expR << q
}

/** Convert a Midnight tick into a WAD zero-coupon price (rounded to the step). */
export function tickToPrice(tick: bigint): bigint {
  if (tick < 0n || tick > MAX_TICK)
    throw new Error(`midnight tick out of range: ${tick}`)

  const exponent = LN_ONE_PLUS_DELTA * (MAX_TICK / 2n - tick)
  const rawPrice = divHalfDownUnchecked(WAD_SQUARED, WAD + wExp(exponent))

  return divHalfDownUnchecked(rawPrice, PRICE_ROUNDING_STEP) * PRICE_ROUNDING_STEP
}

/** Convert a Midnight tick into a WAD fixed *period* rate (`1/price - 1`). */
export function tickToRate(tick: bigint): bigint {
  const price = tickToPrice(tick)
  if (price === 0n) throw new Error('midnight tick price is zero')
  return mulDivUp(WAD, WAD, price) - WAD
}

/**
 * Convert a Midnight tick into a WAD simple APR, annualizing the period rate
 * over `timeToMaturity` seconds. Returns 0 for non-positive time-to-maturity
 * (matured / mispriced markets) rather than throwing.
 */
export function tickToApr(tick: bigint, timeToMaturity: bigint): bigint {
  if (timeToMaturity <= 0n) return 0n
  return mulDivUp(tickToRate(tick), SECONDS_PER_YEAR, timeToMaturity)
}

/** APR as a plain fraction (e.g. `0.0512` for 5.12%). Convenience for display. */
export function tickToAprNumber(tick: bigint, timeToMaturity: bigint): number {
  return Number(tickToApr(tick, timeToMaturity)) / 1e18
}

/**
 * Midnight max liquidation-incentive factor (LIF), WAD-scaled, mirroring the
 * on-chain `ConstantsLib.maxLif`:
 *   `LIF = WAD² / (WAD − liquidationCursor·(WAD − lltv)/WAD)`
 * i.e. `1 / (1 − cursor·(1 − lltv))`. Unlike Morpho Blue, Midnight makes the
 * `liquidationCursor` a per-collateral market parameter and applies NO fixed
 * cap (Morpho hardcodes cursor = 0.3 and caps LIF at 1.15). Inputs are WAD.
 */
export function midnightMaxLif(lltv: bigint, liquidationCursor: bigint): bigint {
  const denom = WAD - (liquidationCursor * (WAD - lltv)) / WAD
  if (denom <= 0n) return 0n
  return (WAD * WAD) / denom
}

/**
 * Midnight liquidation penalty as a plain fraction (`LIF − 1`, e.g. `0.0438`
 * for a 4.38% liquidator bonus) — the value surfaced as `liquidationPenalty` in
 * the public store. Derived from the market's own `liquidationCursor`, so it
 * stays correct if a market deviates from the 0.3 cursor the Morpho
 * approximation assumes. Returns 0 when either input is non-positive.
 */
export function midnightLiquidationPenaltyNumber(
  lltv: bigint,
  liquidationCursor: bigint,
): number {
  if (lltv <= 0n || liquidationCursor <= 0n) return 0
  const lif = midnightMaxLif(lltv, liquidationCursor)
  if (lif <= WAD) return 0
  return Number(lif - WAD) / 1e18
}

// Settlement-fee TTM breakpoints (seconds): [0d, 1d, 7d, 30d, 90d, 180d, 360d].
// Mirrors ConstantsLib settlement-fee indices. One cbp = 1e-6 WAD.
const SETTLEMENT_FEE_BREAKPOINTS_SECS = [
  0, 86_400, 604_800, 2_592_000, 7_776_000, 15_552_000, 31_104_000,
]
const CBP_TO_FRACTION = 1e-6

/**
 * Effective Midnight settlement fee as a plain fraction (e.g. `0.005` = 50 bps)
 * for a given time-to-maturity, mirroring the on-chain piecewise-linear curve
 * over the 7 cbp breakpoints. Post-maturity (ttm ≤ 0) uses the 0d breakpoint;
 * ttm ≥ 360d uses the 360d breakpoint; in between it linearly interpolates.
 * Returns 0 when the fee array is missing/short.
 */
export function midnightSettlementFeeForTtm(
  settlementFeeCbp: number[] | undefined,
  ttmSecs: number,
): number {
  const cbp = settlementFeeCbp
  if (!cbp || cbp.length < 7) return 0
  const bps = SETTLEMENT_FEE_BREAKPOINTS_SECS
  if (ttmSecs <= 0) return cbp[0] * CBP_TO_FRACTION
  if (ttmSecs >= bps[6]) return cbp[6] * CBP_TO_FRACTION
  let i = 0
  while (i < 6 && ttmSecs > bps[i + 1]) i++
  const spanLo = bps[i]
  const spanHi = bps[i + 1]
  const t = (ttmSecs - spanLo) / (spanHi - spanLo)
  const feeCbp = cbp[i] + (cbp[i + 1] - cbp[i]) * t
  return feeCbp * CBP_TO_FRACTION
}

/**
 * Annualized Midnight continuous fee as a PERCENT (e.g. `1` = 1%/yr). The raw
 * value is a per-second WAD rate (uint32); annual fraction =
 * raw·SECONDS_PER_YEAR/1e18. Returns 0 for missing/zero input.
 */
export function midnightContinuousFeeAprPercent(
  continuousFeeRaw: string | bigint | undefined,
): number {
  if (continuousFeeRaw == null) return 0
  let raw: bigint
  try {
    raw = typeof continuousFeeRaw === 'bigint' ? continuousFeeRaw : BigInt(continuousFeeRaw)
  } catch {
    return 0
  }
  if (raw <= 0n) return 0
  return (Number(raw * SECONDS_PER_YEAR) / 1e18) * 100
}
